// Import Libraries
import React from "react";
import PageSEO from "../../../../Components/SEO/PageSEO";

// Import Components
import ServerPageIntro from "../../../../Components/ServerPageIntro/ServerPageIntro";

// Import Animations
import FadeInOnScroll from "../../../../util/Animations/FadeInOnScroll";

// import Assets
import ColocationImage from "../../../../Assets/colocationImage.png";
import ColocationQuoteForm from "./Components/ColocationQuoteForm/ColocationQuoteForm";
import ColocationPricing from "./Components/ColocationPricing/ColocationPricing";
import ColocationInfo from "./Components/ColcoationInfo/ColocationInfo";

const ColocationPage = () => {
  // Define structured data for colocation services
  const colocationSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "WebNX Colocation Services",
    "description": "Enterprise-grade colocation services in Ogden, Utah and Los Angeles, California with N+1 redundancy, diverse power feeds, and carrier-neutral connectivity. From single servers to private cages, we offer scalable solutions with 24/7 security and support.",
    "provider": {
      "@type": "Organization",
      "name": "WebNX",
      "url": "https://webnx.com"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Ogden",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "UT",
          "addressCountry": "US"
        }
      },
      {
        "@type": "City",
        "name": "Los Angeles",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "CA",
          "addressCountry": "US"
        }
      }
    ],
    "offers": {
      "@type": "AggregateOffer",
      "offers": [
        {
          "@type": "Offer",
          "name": "1U/2U Server Colocation - Ogden",
          "description": "1 Amp 208V Power, 1Gbps Unmetered, Remote Hands Support",
          "price": "45.00",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "Full Cabinet - Ogden",
          "description": "42U Space, 30A A+B Power, 10G Unmetered",
          "price": "899.00",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "1U/2U Server Colocation - Los Angeles",
          "description": "1 Amp 208V Power, 10G port 50TB BW, Premium LA Location",
          "price": "119.00",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "Full Cabinet - Los Angeles",
          "description": "42U Space, 30A A+B Power, 10G Unmetered",
          "price": "1500.00",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      ]
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Colocation Services",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Power Options",
          "itemListElement": ["120V 20A", "208V 30A", "208V 50A", "Custom Power"]
        },
        {
          "@type": "OfferCatalog",
          "name": "Network Options",
          "itemListElement": ["1 Gbps", "10 Gbps", "Custom Bandwidth"]
        },
        {
          "@type": "OfferCatalog",
          "name": "Space Options",
          "itemListElement": ["1U/2U", "4U", "Half Cabinet", "Full Cabinet", "Private Cage"]
        }
      ]
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Power Redundancy",
        "value": "N+1 with dual power feeds and UPS backup"
      },
      {
        "@type": "PropertyValue",
        "name": "Network Connectivity",
        "value": "Carrier-neutral with multiple 100Gbps uplinks"
      },
      {
        "@type": "PropertyValue",
        "name": "Security",
        "value": "24/7 monitoring, biometric access, CCTV surveillance"
      },
      {
        "@type": "PropertyValue",
        "name": "Support",
        "value": "24/7/365 on-site technical support"
      }
    ]
  };

  return (
    <>
      <PageSEO
        title="Enterprise Colocation Services | Secure Data Center Solutions | WebNX"
        description="Deploy your infrastructure in WebNX's enterprise-grade data centers. Featuring N+1 redundancy, carrier-neutral connectivity, 24/7 security, and flexible colocation options from 1U to full cabinets in Ogden, UT and Los Angeles, CA."
        keywords="colocation services, data center colocation, server colocation, enterprise colocation, Ogden data center, Los Angeles data center, carrier neutral facility, N+1 redundancy, secure colocation, WebNX colocation"
        path="/colocation"
        image="https://webnx.com/assets/colocation-facility.jpg"
        schema={colocationSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <FadeInOnScroll>
        <ServerPageIntro
          header="Enterprise-Grade Colocation Services"
          subheader="Scalable & Secure Data Center Solutions"
          introText="WebNX provides secure, high-performance colocation in world-class data centers with full N+1 redundancy, diverse power feeds, and direct connectivity to top carriers. From single servers to private cages, our scalable solutions ensure low latency, high uptime, and cost-effective performance."
          image={ColocationImage}
          hideImageOnMobile={true}
          features={[
            {
              icon: "FaShieldAlt",
              title: "High-Security Facilities",
              description: "24/7 monitoring, biometric access, and redundant power for maximum security."
            },
            {
              icon: "FaNetworkWired",
              title: "Redundant Networking",
              description: "Carrier-neutral connectivity with multi-100Gbps uplinks for low-latency routing."
            },
            {
              icon: "FaBolt",
              title: "N+1 Power Redundancy",
              description: "Diverse power feeds with UPS and generator backup to prevent downtime."
            },
            {
              icon: "FaServer",
              title: "Scalable Colocation Options",
              description: "From 1U servers to full private cages, built for flexibility and growth."
            },
            {
              icon: "FaChartLine",
              title: "99.99% Uptime SLA",
              description: "High-availability infrastructure with real-time monitoring and performance guarantees."
            },
            {
              icon: "FaDatabase",
              title: "Direct Cloud & Carrier Access",
              description: "Seamless connectivity to AWS, Google Cloud, Azure, and leading carriers."
            }
          ]}
        />
      </FadeInOnScroll>

      <ColocationPricing />
      <ColocationInfo />
    </>
  );
};

export default ColocationPage;
